import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CalendarIcon, Loader2, Upload, Camera, X } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { toast } from '@/components/ui/use-toast';
import { useVehiclesStore } from '@/store/vehiclesStore';

// Schéma de validation
const vehicleSchema = z.object({
  plate_number: z.string()
    .min(1, 'Le numéro de plaque est requis')
    .regex(/^[A-Z0-9]{2,3}-[A-Z0-9]{2,4}-[A-Z0-9]{2,3}$/, 'Format de plaque invalide. Utilisez le format: AB-123-CD'),
  vehicle_type: z.string().min(1, 'Le type de véhicule est requis'),
  make: z.string().min(1, 'La marque est requise').max(50, 'La marque ne peut pas dépasser 50 caractères'),
  model: z.string().min(1, 'Le modèle est requis').max(50, 'Le modèle ne peut pas dépasser 50 caractères'),
  year: z.number()
    .min(1900, 'L\'année doit être supérieure à 1900')
    .max(new Date().getFullYear() + 1, 'L\'année ne peut pas être dans le futur'),
  capacity_volume: z.union([
    z.string().transform((val) => val === '' ? undefined : parseFloat(val)),
    z.number()
  ])
    .refine((val) => val === undefined || (val >= 0 && val <= 1000), {
      message: 'La capacité volumique doit être entre 0 et 1000 m³'
    })
    .optional(),
  capacity_weight: z.union([
    z.string().transform((val) => val === '' ? undefined : parseFloat(val)),
    z.number()
  ])
    .refine((val) => val === undefined || (val >= 0 && val <= 100000), {
      message: 'La capacité de poids doit être entre 0 et 100 000 kg'
    })
    .optional(),
  insurance_valid_until: z.date().optional(),
  technical_control_until: z.date().optional(),
  image: z.any().optional(),
});

const VehicleFormDialog = ({ isOpen, onClose, onSubmit, vehicle, transporterId }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const { loadVehicleTypes: getVehicleTypes } = useVehiclesStore();

  const form = useForm({
    resolver: zodResolver(vehicleSchema),
    defaultValues: {
      plate_number: '',
      vehicle_type: '',
      make: '',
      model: '',
      year: new Date().getFullYear(),
      capacity_volume: '',
      capacity_weight: '',
      insurance_valid_until: undefined,
      technical_control_until: undefined,
      image: undefined,
    },
  });

  // Charger les types de véhicules
  useEffect(() => {
    if (isOpen) {
      loadVehicleTypes();
    }
  }, [isOpen]);

  // Charger les données du véhicule en mode édition
  useEffect(() => {
    if (isOpen && vehicle && vehicleTypes.length > 0) {
      // Trouver le type de véhicule correspondant
      const vehicleTypeCode = vehicle.vehicle_type?.code || vehicle.vehicle_type;
      const selectedType = vehicleTypes.find(type => type.code === vehicleTypeCode);
      
      form.reset({
        plate_number: vehicle.plate_number || '',
        vehicle_type: selectedType?.code || vehicleTypeCode || '',
        make: vehicle.make || '',
        model: vehicle.model || '',
        year: vehicle.year || new Date().getFullYear(),
        capacity_volume: vehicle.capacity_volume || '',
        capacity_weight: vehicle.capacity_weight || '',
        insurance_valid_until: vehicle.insurance_valid_until ? new Date(vehicle.insurance_valid_until) : undefined,
        technical_control_until: vehicle.technical_control_until ? new Date(vehicle.technical_control_until) : undefined,
        image: vehicle.image || undefined,
      });
    } else if (isOpen && !vehicle) {
      form.reset({
        plate_number: '',
        vehicle_type: '',
        make: '',
        model: '',
        year: new Date().getFullYear(),
        capacity_volume: '',
        capacity_weight: '',
        insurance_valid_until: undefined,
        technical_control_until: undefined,
        image: undefined,
      });
    }
  }, [isOpen, vehicle, vehicleTypes, form]);

  const loadVehicleTypes = async () => {
    try {
      setLoadingTypes(true);
      const response = await getVehicleTypes();
      setVehicleTypes(response || []);
    } catch (error) {
      console.error('Erreur lors du chargement des types de véhicules:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les types de véhicules',
        variant: 'destructive',
      });
    } finally {
      setLoadingTypes(false);
    }
  };

  // Gérer l'upload d'image avec redimensionnement
  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Vérifier la taille du fichier (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast({
          title: 'Erreur',
          description: 'L\'image ne doit pas dépasser 2MB',
          variant: 'destructive',
        });
        return;
      }

      // Vérifier le type de fichier
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Erreur',
          description: 'Veuillez sélectionner un fichier image (JPG, PNG, GIF)',
          variant: 'destructive',
        });
        return;
      }

      // Redimensionner l'image pour optimiser l'affichage
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Définir les dimensions maximales
        const maxWidth = 800;
        const maxHeight = 600;
        
        let { width, height } = img;
        
        // Calculer les nouvelles dimensions en gardant le ratio
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        
        // Redimensionner l'image
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convertir en blob
        canvas.toBlob((blob) => {
          if (blob) {
            // Créer un nouveau fichier avec l'image redimensionnée
            const resizedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            
            form.setValue('image', resizedFile);
            
            // Créer un aperçu
            const reader = new FileReader();
            reader.onload = (e) => {
              setImagePreview(e.target.result);
            };
            reader.readAsDataURL(resizedFile);
            
            toast({
              title: 'Succès',
              description: 'Image redimensionnée et optimisée',
            });
          }
        }, 'image/jpeg', 0.8);
      };
      
      img.src = URL.createObjectURL(file);
    }
  };


  const handleSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      
      let submitData;
      
      // Si il y a une image (nouveau fichier), utiliser FormData, sinon JSON
      if (data.image && data.image instanceof File) {
        // Utiliser FormData pour les images
        submitData = new FormData();
        
        // Champs à exclure (read-only)
        const excludeFields = ['id', 'logiconnect_id', 'created_at', 'updated_at', 'transporter_name'];
        
        // Ajouter tous les champs au FormData
        Object.keys(data).forEach(key => {
          // Ignorer les champs read-only
          if (excludeFields.includes(key)) return;
          
          if (data[key] !== undefined && data[key] !== null) {
            if (key === 'insurance_valid_until' || key === 'technical_control_until') {
              // Convertir les dates en format ISO string
              if (data[key]) {
                submitData.append(key, data[key].toISOString().split('T')[0]);
              }
            } else if (key === 'image' && data[key] instanceof File) {
              // Ajouter le fichier image
              submitData.append(key, data[key]);
            } else if (key === 'capacity_volume' || key === 'capacity_weight' || key === 'year') {
              // Convertir les nombres en chaîne pour FormData
              if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
                submitData.append(key, data[key].toString());
              }
            } else {
              submitData.append(key, data[key]);
            }
          }
        });
        
        // Ajouter le transporteur
        submitData.append('transporter', transporterId);
      } else {
        // Utiliser JSON pour les données sans image
        submitData = {
          ...data,
          transporter: transporterId
        };
        
        // Champs à exclure (read-only)
        const excludeFields = ['id', 'logiconnect_id', 'created_at', 'updated_at', 'transporter_name'];
        excludeFields.forEach(field => {
          delete submitData[field];
        });
        
        // Nettoyer les valeurs vides et undefined, mais garder les champs requis
        const requiredFields = ['plate_number', 'vehicle_type'];
        Object.keys(submitData).forEach(key => {
          if (submitData[key] === undefined || submitData[key] === null || 
              (submitData[key] === '' && !requiredFields.includes(key))) {
            delete submitData[key];
          }
        });
        
        // Supprimer l'image si c'est une URL (pas un nouveau fichier)
        if (submitData.image && !(submitData.image instanceof File)) {
          delete submitData.image;
        }
        
        // Convertir les valeurs numériques
        if (submitData.year) {
          submitData.year = parseInt(submitData.year);
        }
        if (submitData.capacity_volume) {
          submitData.capacity_volume = parseFloat(submitData.capacity_volume);
        }
        if (submitData.capacity_weight) {
          submitData.capacity_weight = parseFloat(submitData.capacity_weight);
        }
        
        // Convertir les dates en format ISO string
        if (submitData.insurance_valid_until) {
          submitData.insurance_valid_until = submitData.insurance_valid_until.toISOString().split('T')[0];
        }
        if (submitData.technical_control_until) {
          submitData.technical_control_until = submitData.technical_control_until.toISOString().split('T')[0];
        }
      }
      
      // Debug: Afficher les données envoyées
      console.log('🚗 Données du véhicule à envoyer:', submitData);
      console.log('📋 Données brutes du formulaire:', data);
      
      await onSubmit(submitData);
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      
      // Gérer les erreurs de validation du backend
      if (error.type === 'validation' && error.details) {
        // Mapper les erreurs du backend sur les champs du formulaire
        const fieldErrors = {};
        Object.keys(error.details).forEach(fieldName => {
          const errorMessage = Array.isArray(error.details[fieldName]) 
            ? error.details[fieldName][0] 
            : error.details[fieldName];
          fieldErrors[fieldName] = errorMessage;
        });
        
        // Définir les erreurs sur le formulaire
        Object.keys(fieldErrors).forEach(fieldName => {
          form.setError(fieldName, {
            type: 'manual',
            message: fieldErrors[fieldName]
          });
        });
        
        // Afficher un toast avec le message général
        toast({
          title: 'Erreur de validation',
          description: error.message || 'Veuillez corriger les erreurs dans le formulaire',
          variant: 'destructive',
        });
        
        return; // Ne pas relancer l'erreur
      }
      
      // Pour les autres erreurs, laisser le composant parent les gérer
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {vehicle ? 'Modifier le véhicule' : 'Ajouter un nouveau véhicule'}
          </DialogTitle>
          <DialogDescription>
            {vehicle ? 'Modifiez les informations du véhicule' : 'Renseignez les informations du nouveau véhicule'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Numéro de plaque */}
              <FormField
                control={form.control}
                name="plate_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Numéro de plaque *</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Ex: AB-123-CD" 
                        {...field}
                        onChange={(e) => {
                          // Convertir automatiquement en majuscules
                          const value = e.target.value.toUpperCase();
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                    <FormDescription>
                      Format requis: 2-3 lettres/chiffres, tiret, 2-4 lettres/chiffres, tiret, 2-3 lettres/chiffres
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Type de véhicule */}
              <FormField
                control={form.control}
                name="vehicle_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type de véhicule *</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner un type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {loadingTypes ? (
                          <SelectItem value="loading" disabled>
                            Chargement des types...
                          </SelectItem>
                        ) : (
                          vehicleTypes.map((type) => (
                            <SelectItem key={type.code} value={type.code}>
                              {type.label}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Marque */}
              <FormField
                control={form.control}
                name="make"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Marque *</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: Toyota" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Modèle */}
              <FormField
                control={form.control}
                name="model"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Modèle *</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: Hilux" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Année */}
              <FormField
                control={form.control}
                name="year"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Année *</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="Ex: 2023"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Capacité volumique */}
              <FormField
                control={form.control}
                name="capacity_volume"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Capacité volumique (m³)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        step="0.1"
                        placeholder="Ex: 10.5"
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Capacité de poids */}
              <FormField
                control={form.control}
                name="capacity_weight"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Capacité de poids (kg)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        step="0.1"
                        placeholder="Ex: 1500"
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Validité assurance */}
              <FormField
                control={form.control}
                name="insurance_valid_until"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Validité assurance</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP", { locale: fr })
                            ) : (
                              <span>Sélectionner une date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Validité contrôle technique */}
              <FormField
                control={form.control}
                name="technical_control_until"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Validité contrôle technique</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP", { locale: fr })
                            ) : (
                              <span>Sélectionner une date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Section Image */}
            <div className="space-y-6 pt-6 border-t">
              <h4 className="text-lg font-medium">Image du véhicule</h4>
              
              <div className="space-y-4">
                {/* Aperçu de l'image */}
                {(imagePreview || vehicle?.image) && (
                  <div className="relative w-full h-64 bg-muted rounded-lg overflow-hidden">
                    <img
                      src={imagePreview || vehicle?.image}
                      alt="Aperçu du véhicule"
                      className="w-full h-full object-cover"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => {
                        setImagePreview(null);
                        form.setValue('image', undefined);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                
                {/* Bouton d'upload */}
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                    id="vehicle-image"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('vehicle-image').click()}
                    className="flex items-center gap-2"
                  >
                    <Camera className="h-4 w-4" />
                    {imagePreview || vehicle?.image ? 'Changer l\'image' : 'Ajouter une image'}
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Formats acceptés: JPG, PNG, GIF (max 2MB) - L'image sera automatiquement redimensionnée
                  </p>
                </div>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Annuler
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {vehicle ? 'Modifier' : 'Ajouter'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default VehicleFormDialog;
